import { HomeIcon } from '@heroicons/react/20/solid'
import { useEffect } from 'react'
import { FiTarget } from 'react-icons/fi'
import { useParams } from 'react-router'
// import { useParams } from 'react-router'
import ReactFlow, {
  Background,
  BackgroundVariant,
  ControlButton,
  Controls,
  Edge,
  Node,
  NodeOrigin,
  ProOptions,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow'
import { useShallow } from 'zustand/react/shallow'

import { Pipeline } from '@ui/__generated__/graphql'
import { Button } from '@ui/components/core'
import { Spinner } from '@ui/components/loaders'
import { NodeTypeEnum } from '@ui/interfaces'
import useAppNavigation from '@ui/lib/hooks/useAppNavigation'
// import useAppNavigation from '@ui/lib/hooks/useAppNavigation'
import { useAppSearchParams } from '@ui/lib/hooks/useAppSearchParams'
import useAutoLayout from '@ui/lib/hooks/useLayout'
import edgeTypes from '@ui/pages/pipelinesWorkflow/components/EdgeTypes'
import nodeTypes from '@ui/pages/pipelinesWorkflow/components/NodeTypes'
import { RouteNamesEnum } from '@ui/router/routes'
// import { RouteNamesEnum } from '@ui/router/routes'
import { useNodeAndEdgeStore } from '@ui/stores'

const proOptions: ProOptions = { account: 'paid-pro', hideAttribution: true }
const nodeOrigin: NodeOrigin = [0, 0]

interface PipelineMiniMapProps {
  selectedPipeline: Pipeline | null
  nodes: Node[]
  edges: Edge[]
  currentNodeId: string | null
  setCurrentNodeId: (id: string | null) => void
}

const MiniMapFlow = ({ selectedPipeline, nodes, edges, currentNodeId, setCurrentNodeId }: PipelineMiniMapProps) => {
  // const { instanceId } = useParams<{ instanceId: string }>()
  const { searchParams, updateSearchParams } = useAppSearchParams()

  const { fitView } = useReactFlow()

  // const { navigateTo } = useAppNavigation()

  const setToFirehoseNode = () => {
    fitView({ nodes: [{ id: 'firehose' }], duration: 400, maxZoom: 1.25 })
    setCurrentNodeId(null)
  }

  const targetSelectedNode = () => {
    if (!currentNodeId) return
    fitView({ nodes: [{ id: currentNodeId }], duration: 400, maxZoom: 1.25 })
  }

  // const navigateToPipeline = () => {
  //   if (!selectedPipeline) return

  //   navigateTo(RouteNamesEnum.EXISTING_PIPELINE, {
  //     instanceId,
  //     pipelineId: selectedPipeline.id,
  //   })
  // }

  // const navigateToNode = (nodeId: string) => {
  //   navigateTo(RouteNamesEnum.EXISTING_PIPELINE, {}, { nodeId: nodeId })
  // }

  useEffect(() => {
    const nodeId = searchParams.get('nodeId')
    if (nodeId && nodeId !== currentNodeId) {
      setCurrentNodeId(nodeId)
    }
  }, [])

  useEffect(() => {
    if (searchParams) {
      updateSearchParams({ nodeId: currentNodeId, pipelineId: selectedPipeline?.id })
    }
  }, [currentNodeId, searchParams, selectedPipeline])

  // Force a refresh of the mini map when the selected pipeline changes
  useEffect(() => {
    if (selectedPipeline) {
      const firehoseNode = nodes.find((node) => node.type === NodeTypeEnum.FIREHOSE)
      if (firehoseNode) {
        // This needs to be delayed so the nodes and edges are set
        // before we try to fit the view
        setTimeout(() => {
          fitView({ duration: 400, maxZoom: 1.25, nodes: [{ id: firehoseNode.id }] })
        }, 100)
      }
    }
  }, [fitView, nodes, selectedPipeline])

  useAutoLayout()

  return (
    <ReactFlow
      id="pipeline-mini-map"
      fitView
      nodes={nodes}
      edges={edges}
      onInit={() => {
        fitView({ duration: 400, maxZoom: 1.05 })
      }}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      proOptions={proOptions}
      nodeOrigin={nodeOrigin}>
      <Background variant={BackgroundVariant.Dots} />
      <Controls>
        <ControlButton onClick={setToFirehoseNode}>
          <HomeIcon />
        </ControlButton>
        <ControlButton onClick={targetSelectedNode}>
          <FiTarget />
        </ControlButton>
      </Controls>

      {/* <div className="absolute bottom-0 right-0 z-10 flex flex-row">
        {selectedPipeline && currentNodeId && (
          <Button onClick={() => navigateToNode(currentNodeId.toString())}>Edit Node</Button>
        )}
        <Button onClick={navigateToPipeline}>View Pipeline</Button>
      </div> */}
    </ReactFlow>
  )
}

export function PipelineMiniMap({
  selectedPipeline,
  loading,
}: {
  selectedPipeline: Pipeline | null
  loading: boolean
}) {
  const { instanceId } = useParams<{ instanceId: string }>()
  const { navigateTo } = useAppNavigation()

  const { nodes, edges, currentNodeId, setCurrentNodeId } = useNodeAndEdgeStore(
    useShallow((state) => ({
      nodes: state.nodes,
      edges: state.edges,
      currentNodeId: state.currentNodeId,
      setCurrentNodeId: state.setCurrentNodeId,
    }))
  )

  // if (nodes.length === 0) return null

  return (
    <div className="h-full w-full">
      <div className="flex flex-row items-center justify-between rounded-t border-b border-gray-200 bg-white p-3">
        <p className="text-sm font-semibold">Minimap</p>

        <Button
          size="small"
          variant="secondary"
          onClick={() =>
            navigateTo(RouteNamesEnum.EXISTING_PIPELINE, {
              pipelineId: selectedPipeline?.id,
              instanceId,
            })
          }>
          Go to Pipeline
        </Button>
      </div>

      {loading || !selectedPipeline || !currentNodeId ? (
        <div className="size-full flex items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="h-[calc(100%-3.5rem)] w-full">
          <ReactFlowProvider>
            <MiniMapFlow
              selectedPipeline={selectedPipeline}
              nodes={nodes}
              edges={edges}
              currentNodeId={currentNodeId}
              setCurrentNodeId={setCurrentNodeId}
            />
          </ReactFlowProvider>
        </div>
      )}
    </div>
  )
}
